import { createFileRoute, Outlet } from "@tanstack/react-router";
import { appBottomClearanceClass } from "@/components/common/appBottomClearance";
import { AppPageLayout } from "@/components/common/AppPageLayout";
import { usersOrganisationsRolesQueryOptions } from "@/features/auth/api/selectUsersOrganisationsRoles";
import { redirectIfNeedsPasswordSetup } from "@/features/auth/utils/redirectIfNeedsPassword";
import Nav from "@/features/navigation/components/Navigation";
import JourneyProgress from "@/features/onboarding/components/JourneyProgress";
import { useShouldHideLayoutJourneyProgress } from "@/features/onboarding/hooks/useShouldHideLayoutJourneyProgress";
import useAuthQuery from "../features/auth/api/useAuthQuery";
import { useAuth } from "../utils/auth";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { ui } from "@patient-watch/ui";

export const Route = createFileRoute("/onboarding")({
    component: LayoutComponent,
    beforeLoad: async ({ context }) => {
        await redirectIfNeedsPasswordSetup(context?.queryClient, context?.auth?.id);
    },
    loader: async ({ context }) => {
        const roles = context?.auth?.id
            ? await context?.queryClient?.ensureQueryData(usersOrganisationsRolesQueryOptions(context.auth.id))
            : [];
        if (roles) {
            context?.auth?.setRoles(roles);
        }
    },
});

function LayoutComponent() {
    const auth = useAuth();
    const copy = useProfileCopy();
    const { refetch, isLoading } = useAuthQuery();
    const hideLayoutJourneyProgress = useShouldHideLayoutJourneyProgress();
    const showJourneyCard = Boolean(auth?.isAuthenticated && auth.roles?.isPatient && !hideLayoutJourneyProgress);

    if (isLoading) {
        return null;
    }

    return (
        <main className="flex flex-col h-dvh overflow-hidden">
            <Nav app={true} />
            <AppPageLayout
                padding="none"
                className={`flex flex-col flex-1 gap-y-6 px-2 pt-6 min-h-0 overflow-y-auto ${appBottomClearanceClass}`}
            >
                {auth?.isAuthenticated ? (
                    <>
                        {showJourneyCard ? <JourneyProgress /> : null}
                        <Outlet />
                    </>
                ) : (
                    <ui.Card>
                        <ui.CardHeader>
                            <ui.CardTitle>{copy.beginOnboarding}</ui.CardTitle>
                            <ui.CardDescription>{copy.onboardingSignedOutDescription}</ui.CardDescription>
                        </ui.CardHeader>
                        <ui.CardFooter>
                            <ui.Button onClick={() => refetch()}>{copy.continue}</ui.Button>
                        </ui.CardFooter>
                    </ui.Card>
                )}
            </AppPageLayout>
        </main>
    );
}
